import React from "react";
import { siteConfig } from "../siteConfig";

// Everything here has to stay true of the site as built. The homepage form
// promises "No list, no sharing, and we delete them if you ask", so this page
// says the same thing in the language a policy needs, and nothing more. If the
// site ever gains analytics, a cookie, a CRM or a mailing list, this page is
// wrong until it is updated in the same commit.
const UPDATED = "16 September 2026";

export default function Privacy() {
  return (
    <main id="main" className="legal-page section-wrap">
      <p className="section-label">Privacy</p>
      <h1>How we handle your information.</h1>
      <p className="legal-lede">
        VecTurf is an early stage golf course intelligence product. This site
        does one thing with personal data: it takes pilot enquiries from the
        form on the homepage and lets us reply to them. Last updated {UPDATED}.
      </p>

      <section>
        <h2>Who we are</h2>
        <p>
          VecTurf is a product being built by its two founders and is not yet a
          registered company. Until it is, the founders are jointly responsible
          for the information collected here, and the contact route below
          reaches them directly. When the company is incorporated this page will
          name it, along with its registered address and company number.
        </p>
      </section>

      <section>
        <h2>What we collect, and why</h2>
        <p>
          Only what you type into the pilot enquiry form: your name, your role,
          your club or course, your email address, your phone number if you
          choose to give it, and whatever you tell us you would want a pilot to
          answer. Name, club and email are required because without them we
          cannot tell who is asking or reply; the rest are optional and marked
          as optional on the form.
        </p>
        <p>
          We use it to reply to you and to have a conversation about a pilot on
          your course. That is the whole purpose. We do not add you to a
          marketing list, we do not sell or share your details with anyone else,
          and we do not use them to build a profile or to advertise to you.
        </p>
        <p>
          Our lawful basis is legitimate interest: you asked us to get in touch
          about a pilot, and replying is what you were expecting. If you would
          rather we did not hold the enquiry at all, say so and we will delete
          it.
        </p>
      </section>

      <section>
        <h2>The application is separate</h2>
        <p>
          This page covers the public website only. If your club is running a
          pilot, the data in the VecTurf application, including your course
          data and the accounts your team uses, is covered by the agreement we
          make with your club rather than by this page.
        </p>
      </section>

      <section>
        <h2>Who else can see it</h2>
        <p>
          The site is hosted by Netlify, which also receives the enquiry form
          and stores submissions so we can read them. Netlify therefore
          processes your enquiry on our behalf, and its own privacy terms apply
          to that processing. This may involve your information being handled
          outside the UK.
        </p>
        <p>
          Page fonts are served by Google Fonts, which means your browser
          requests a font file from Google and Google sees the request. No other
          third party is involved: there is no analytics, no advertising network
          and no social media tracking on this site.
        </p>
        <p>
          We may use ordinary email and calendar tools to reply to you and
          arrange a conversation, in the same way any correspondence works.
        </p>
      </section>

      <section>
        <h2>Cookies</h2>
        <p>
          This site sets no cookies and uses no analytics or tracking of any
          kind. That is also why you are not being asked to accept anything.
          Netlify may set a strictly necessary cookie to keep the site running
          and to spot abusive traffic.
        </p>
      </section>

      <section>
        <h2>How long we keep it</h2>
        <p>
          While a pilot conversation is live, and for up to twelve months after
          our last contact, so that we can pick the conversation back up. After
          that we delete it. If you ask us to delete it sooner, we will, and we
          are not required to keep a copy.
        </p>
      </section>

      <section>
        <h2>Your rights</h2>
        <p>
          Under UK data protection law you can ask us for a copy of what we
          hold, ask us to correct it, ask us to delete it, or object to our
          using it. Ask and we will do it, normally within a month, and there is
          nothing to pay. Because we hold very little, usually a single form
          submission and an email thread, these requests are quick to handle.
        </p>
        <p>
          If you think we have handled your information badly, please tell us
          first so we can put it right. You also have the right to complain to
          the Information Commissioner's Office, the UK regulator, at ico.org.uk
          or on 0303 123 1113.
        </p>
      </section>

      <section>
        <h2>Security, honestly stated</h2>
        <p>
          Enquiries are held in Netlify behind an account only the founders can
          reach, and the site is served over HTTPS. No small team can promise
          that nothing will ever go wrong; what we can say is that we collect
          little, keep it briefly and do not spread it around, which is the part
          that actually reduces the risk to you.
        </p>
      </section>

      <section>
        <h2>Contact</h2>
        <p>
          {siteConfig.enquiryUrl ? (
            <>
              For anything on this page, including a request to delete your
              enquiry, email{" "}
              <a className="text-link-inline" href={siteConfig.enquiryUrl}>
                {siteConfig.contactEmail || "us"}
              </a>
              . It reaches both founders.
            </>
          ) : (
            <>
              A monitored contact address is being set up. Until it is listed
              here, reply to any email you have had from us and the request will
              reach the founders.
            </>
          )}
        </p>
      </section>

      <section>
        <h2>Changes</h2>
        <p>
          If what we collect or what we do with it changes, we update this page
          and change the date at the top. We will not quietly start doing
          something with your enquiry that this page says we do not do.
        </p>
      </section>

      <p className="legal-back">
        <a className="text-link" href="/">
          Back to the homepage <span aria-hidden="true">↗</span>
        </a>
      </p>
    </main>
  );
}
